/**
 * Auth Guard for Protected Routes
 *
 * Provides route guards for:
 * - Requiring authentication (redirects to login if not authenticated)
 * - Requiring specific roles (admin, assistant)
 * - Preventing authenticated users from accessing login page
 */

import type { RouteContext, RouteGuard } from './router';
import { getAuthState, logout as logoutStore } from './auth-store';

/**
 * Check if user is authenticated based on current state
 */
function isCurrentlyAuthenticated(): boolean {
  const state = getAuthState();
  return state.isAuthenticated && !state.isLoading;
}

/**
 * Check if auth is currently loading
 */
function isAuthLoading(): boolean {
  const state = getAuthState();
  return state.isLoading;
}

/**
 * Get current user role
 */
function getCurrentUserRole(): string | null {
  const state = getAuthState();
  return state.user?.role ?? null;
}

/**
 * Route guard that requires authentication
 * Redirects to /login if user is not authenticated
 *
 * @returns RouteGuard function
 */
export function requireAuth(): RouteGuard {
  return (_context: RouteContext): boolean | string => {
    // If still loading auth, allow navigation (will be handled by app init)
    if (isAuthLoading()) {
      return true;
    }

    if (!isCurrentlyAuthenticated()) {
      return '/login';
    }

    return true;
  };
}

/**
 * Route guard that requires a specific role or one of multiple roles
 * Redirects to home or login if user doesn't have the required role
 *
 * @param role - Required role (string) or array of allowed roles
 * @param redirectTo - Path to redirect to if role check fails (default: '/')
 * @returns RouteGuard function
 */
export function requireRole(role: string | string[], redirectTo = '/'): RouteGuard {
  return (_context: RouteContext): boolean | string => {
    // If still loading auth, allow navigation
    if (isAuthLoading()) {
      return true;
    }

    if (!isCurrentlyAuthenticated()) {
      return '/login';
    }

    const userRole = getCurrentUserRole();
    const allowedRoles = Array.isArray(role) ? role : [role];

    if (!userRole || !allowedRoles.includes(userRole)) {
      return redirectTo;
    }

    return true;
  };
}

/**
 * Route guard that requires admin role
 * Redirects to home if user is not an admin
 *
 * @param redirectTo - Path to redirect to if not admin (default: '/')
 * @returns RouteGuard function
 */
export function requireAdmin(redirectTo = '/'): RouteGuard {
  return requireRole('ADMIN', redirectTo);
}

/**
 * Route guard that prevents authenticated users from accessing the route
 * Useful for login page - redirects to dashboard if already authenticated
 *
 * @param redirectTo - Path to redirect authenticated users to (default: '/dashboard')
 * @returns RouteGuard function
 */
export function requireGuest(redirectTo = '/dashboard'): RouteGuard {
  return (context: RouteContext): boolean | string => {
    // Check for session_expired flag - if present, clear any stale auth state
    // This prevents infinite redirect loops when session expires
    if (context.query.session_expired === '1') {
      // Force clear the auth state to ensure we don't have stale data
      logoutStore();
      // Clean up the URL by removing the query parameter (optional but cleaner UX)
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.delete('session_expired');
        window.history.replaceState({}, '', url.pathname);
      }
      return true;
    }

    // If still loading auth, allow navigation
    if (isAuthLoading()) {
      return true;
    }

    if (isCurrentlyAuthenticated()) {
      return redirectTo;
    }

    return true;
  };
}

/**
 * Combine multiple guards - all must pass
 * Returns the redirect from the first guard that fails
 *
 * @param guards - Array of guards to combine
 * @returns Combined RouteGuard function
 */
export function combineGuards(...guards: RouteGuard[]): RouteGuard {
  return async (context: RouteContext): Promise<boolean | string> => {
    for (const guard of guards) {
      const result = await guard(context);
      if (result !== true) {
        return result;
      }
    }
    return true;
  };
}

/**
 * Global auth guard for use in router initialization
 * Allows access to public routes and auth routes, requires auth for others
 */
export function createGlobalAuthGuard(publicPaths: string[] = []): RouteGuard {
  // Default public paths
  const allPublicPaths = [
    '/login',
    '/auth/callback',
    ...publicPaths,
  ];

  return (context: RouteContext): boolean | string => {
    // Allow public paths
    const isPublicPath = allPublicPaths.some(path => {
      if (path.endsWith('*')) {
        return context.path.startsWith(path.slice(0, -1));
      }
      return context.path === path;
    });

    if (isPublicPath) {
      return true;
    }

    // If still loading auth, allow navigation
    if (isAuthLoading()) {
      return true;
    }

    // Require auth for all other paths
    if (!isCurrentlyAuthenticated()) {
      return '/login';
    }

    return true;
  };
}
